/*
    Copyright © 2025 TRIPLES INNOVATION. All rights reserved.
    Proprietary and confidential. This source code is the exclusive property of
    TRIPLES INNOVATION and protected by copyright and trade secret law.
    Any unauthorized use, copying, modification, distribution, or disclosure
    is strictly prohibited without prior written permission from TRIPLES INNOVATION.
    Use is subject to a written license agreement with TRIPLES INNOVATION.
*/

import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    CircularProgress,
    Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDialogForm } from '../../hooks/useDialogForm';

interface MarkIrrelevantDialogProps {
    open: boolean;
    licensePlate: string | null;
    onConfirm: (reason: string, licensePlate?: string) => Promise<void>;
    onCancel: () => void;
    isLoading?: boolean;
}

type MarkIrrelevantFields = {
    licensePlate: string;
    reason: string;
};

const DEFAULT_FIELDS: MarkIrrelevantFields = {
    licensePlate: '',
    reason: '',
};

const REQUIRED_FIELDS_WITH_PLATE = [
    'licensePlate',
    'reason',
] as const satisfies ReadonlyArray<keyof MarkIrrelevantFields>;

const REQUIRED_FIELDS_WITHOUT_PLATE = [
    'reason',
] as const satisfies ReadonlyArray<keyof MarkIrrelevantFields>;

const MarkIrrelevantDialog: React.FC<MarkIrrelevantDialogProps> = ({
    open,
    licensePlate,
    onConfirm,
    onCancel,
    isLoading = false,
}) => {
    const { t } = useTranslation("common");
    const needsPlateInput = licensePlate === null;
    const {
        fields,
        handleChange,
        hasValidRequiredFields,
    } = useDialogForm<MarkIrrelevantFields>({
        config: {
            requiredFields: needsPlateInput ? REQUIRED_FIELDS_WITH_PLATE : REQUIRED_FIELDS_WITHOUT_PLATE,
            defaultValues: DEFAULT_FIELDS,
            additionalValidation: (currentFields) =>
                currentFields.reason.trim().length > 0 &&
                (!needsPlateInput || currentFields.licensePlate.trim().length > 0),
        },
        isOpen: open,
    });

    const handleConfirm = async () => {
        if (needsPlateInput) {
            await onConfirm(fields.reason, fields.licensePlate.trim());
        } else {
            await onConfirm(fields.reason);
        }
    };

    const handleCancel = () => {
        onCancel();
    };

    return (
        <Dialog open={open} onClose={handleCancel} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t("search.markAsIrrelevant")}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    {needsPlateInput && (
                        <TextField
                            fullWidth
                            label={t('administration.irrelevantEntries.licensePlate')}
                            value={fields.licensePlate}
                            onChange={(e) => handleChange('licensePlate', e.target.value)}
                            disabled={isLoading}
                        />
                    )}
                    <TextField
                        fullWidth
                        multiline
                        rows={4}
                        placeholder={t("search.markIrrelevantReasonPlaceholder")}
                        value={fields.reason}
                        onChange={(e) => handleChange('reason', e.target.value)}
                        disabled={isLoading}
                        label={t("search.markIrrelevantReason")}
                        helperText={licensePlate ? t("search.markIrrelevantHelperText", { licensePlate }) : ""}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleCancel} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="outlined"
                    disabled={isLoading || !hasValidRequiredFields}
                >
                    {isLoading ? <CircularProgress size={24} /> : t("common.confirm")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default MarkIrrelevantDialog;
